<template>
    <UIDialog v-if="isOpen" :open="isOpen" @update:open="handleOpenChange">
        <DialogContent class="max-w-md">
            <DialogHeader>
                <DialogTitle>Create Saved Search</DialogTitle>
                <DialogDescription>
                    Save the current Discovery filters so you can load them again later.
                </DialogDescription>
            </DialogHeader>

            <div class="space-y-4">
                <div
                    v-if="duplicateMessage"
                    class="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800"
                >
                    <div class="flex gap-2">
                        <AppIcon
                            name="triangle-exclamation"
                            class="mt-0.5 h-4 w-4 shrink-0 text-amber-600"
                        />
                        <p>{{ duplicateMessage }}</p>
                    </div>
                </div>

                <div
                    v-if="!canSave && saveDisabledReason"
                    class="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800"
                >
                    <div class="flex gap-2">
                        <AppIcon
                            name="triangle-exclamation"
                            class="mt-0.5 h-4 w-4 shrink-0 text-amber-600"
                        />
                        <p>{{ saveDisabledReason }}</p>
                    </div>
                </div>

                <div class="space-y-2">
                    <label class="text-sm font-medium text-slate-700" for="saved-search-name">
                        Saved Search Name
                    </label>
                    <Input
                        id="saved-search-name"
                        ref="titleInput"
                        v-model="title"
                        placeholder='e.g. "MMORPG Players"'
                        :class="{ 'border-red-500 ring-red-500': errorMessage }"
                        @input="resetError"
                    />
                    <p v-if="errorMessage && !duplicateMessage" class="text-xs text-red-700">
                        {{ errorMessage }}
                    </p>
                </div>

                <AppliedFilters :filters="filters" :filter-labels="filtering.labels" :source="source" />
            </div>

            <DialogFooter>
                <Button type="button" variant="outline" @click="handleCancel">
                    Cancel
                </Button>
                <Button
                    type="button"
                    :disabled="isSaveDisabled"
                    @click="handleSave()"
                >
                    <AppIcon
                        v-if="isSaving"
                        name="spinner-third"
                        class="mr-2 h-4 w-4 animate-spin"
                    />
                    Save
                </Button>
            </DialogFooter>
        </DialogContent>
    </UIDialog>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue'
import { useMutation } from '@tanstack/vue-query'
import { isEqual, omit } from 'lodash'
import { notify } from '@kyvg/vue3-notification'
import {
    Dialog as UIDialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { difference } from '@/utils/Utils'
import { axios } from '@/utils/axios'
import AppliedFilters from '@/partials/modals/CreateSavedSearchModal/AppliedFilters.vue'

const props = defineProps({
    isOpen: {
        type: Boolean,
        required: true,
    },
    onClose: {
        type: Function,
        required: true,
    },
    onSave: {
        type: Function,
        required: true,
    },
    onCancel: {
        type: Function,
        required: true,
    },
    filtering: {
        type: Object,
        required: true,
    },
    platform: {
        type: String,
        required: true,
    },
    source: {
        type: Object,
        default: null,
    },
    paging: {
        type: Object,
        default: null,
    },
    resultCount: {
        type: Number,
        default: null,
    },
    countLimit: {
        type: Number,
        required: true,
    },
    canSave: {
        type: Boolean,
        default: true,
    },
    saveDisabledReason: {
        type: String,
        default: '',
    },
})

const titleInput = ref(null)
const title = ref('')
const saveError = ref('')

const getDefaultSort = () => [
    {
        field: props.filtering.current?.content_type === 'stream' ? 'avg_viewers' : 'avg_views',
        direction: 'desc',
        source: 'default',
    },
]

const getChangedFilters = () => Object.keys(props.filtering.current || {}).reduce((results, key) => {
    if (!isEqual(props.filtering.default?.[key], props.filtering.current[key])) {
        results[key] = props.filtering.current[key]
    }

    return results
}, {})

const getSourceQuery = () => {
    if (!props.source || props.source.type === 'platform' || !props.source.input || (Array.isArray(props.source.input) && !props.source.input.length)) {
        return {}
    }

    if (props.source.type === 'mixed') {
        const listIds = Array.isArray(props.source.input.list_id) ? props.source.input.list_id : (props.source.input.list_id ? [props.source.input.list_id] : [])
        const campaignIds = Array.isArray(props.source.input.campaign_id) ? props.source.input.campaign_id : (props.source.input.campaign_id ? [props.source.input.campaign_id] : [])

        return {
            ...(listIds.length ? { list_id: listIds } : {}),
            ...(campaignIds.length ? { campaign_id: campaignIds } : {}),
        }
    }

    const sourceFields = {
        list: 'list_id',
        campaign: 'campaign_id',
    }
    const field = sourceFields[props.source.type]

    return field ? { [field]: props.source.input } : {}
}

const getSourcePayload = () => {
    if (!props.source || props.source.type === 'platform' || !props.source.input || (Array.isArray(props.source.input) && !props.source.input.length)) return null

    return {
        type: props.source.type,
        input: props.source.input,
        name: props.source.name,
    }
}

const getSavedSearchPayload = (savedSearchName) => {
    const filters = {
        ...getChangedFilters(),
        ...getSourceQuery(),
    }

    return {
        name: savedSearchName,
        title: savedSearchName,
        source: getSourcePayload(),
        request: {
            params: {
                platform_id: props.platform,
            },
            body: {
                collection: {
                    content_type: props.filtering.current?.content_type,
                    day_range: String(props.filtering.current?.day_range),
                },
                query: omit(filters, ['content_type', 'day_range']),
                pagination: {
                    limit: props.paging?.perPage || 15,
                    skip: 0,
                    sort: props.paging?.sort?.length ? props.paging.sort : getDefaultSort(),
                },
                countLimit: props.countLimit,
            },
        },
        response: {
            creators: props.resultCount ?? 0,
        },
    }
}

const createSavedSearch = useMutation({
    mutationFn: (data) => {
        const savedSearchName = data.title.trim()
        return axios.post('/discovery/history/saved', getSavedSearchPayload(savedSearchName))
    },
    onSuccess: ({ data }) => {
        const savedSearchName = data?.payload?.search?.name || data?.payload?.name || title.value.trim()
        notify({
            title: 'Saved Search Created',
            text: `Your saved search "${savedSearchName}" has been created.`,
            type: 'success',
        })
    },
})

const isSaving = computed(() => createSavedSearch.isLoading?.value === true || createSavedSearch.isPending?.value === true)
const isSaveDisabled = computed(() => !props.canSave || !title.value.trim() || isSaving.value)

const errorMessage = computed(() => {
    const error = createSavedSearch.error?.value
    const responseMessage = error?.response?.data?.message

    if (responseMessage) return responseMessage
    if (saveError.value) return saveError.value
    if (error?.response?.status) return `Failed to save search (${error.response.status}).`
    return ''
})
const duplicateMessage = computed(() => (
    createSavedSearch.error?.value?.response?.status === 409
        ? errorMessage.value || 'A saved search already exists with these filters.'
        : ''
))

const resetError = () => {
    saveError.value = ''
    createSavedSearch.reset()
}

const resetState = () => {
    title.value = ''
    resetError()
}

const handleClose = () => {
    resetState()
    props.onClose()
}

const handleOpenChange = (open) => {
    if (!open) {
        handleClose()
    }
}

const handleSave = async () => {
    if (!props.canSave) {
        saveError.value = props.saveDisabledReason || 'Results are still loading.'
        return
    }

    try {
        await createSavedSearch.mutateAsync({
            title: title.value,
        })
        props.onSave()
        handleClose()
    } catch (error) {
        saveError.value = error?.response?.data?.message || error?.message || 'Failed to save search.'
        notify({
            title: 'Saved Search Not Created',
            text: saveError.value,
            type: 'error',
        })
    }
}

const handleCancel = () => {
    resetState()
    props.onCancel()
}

const filters = computed(() => difference(props.filtering.current, props.filtering.default))

watch(
    () => props.isOpen,
    async (newValue) => {
        if (newValue) {
            await nextTick()
            const inputElement = titleInput.value?.$el || titleInput.value
            inputElement?.focus?.()
        }
    },
    { immediate: true },
)
</script>
